// Raster engine for the shared invoice PDF pipeline (facade in
// ../invoicePdf.js).
//
// Captures each rendered page element with html2canvas and paints it onto a
// caller-created jsPDF instance as a full-sheet JPEG — one PDF page per
// element. This is the historic pipeline, moved verbatim from invoicePdf.js
// (including the landscape-orientation support); it remains the default
// engine in P1 and the pinned engine for the OPI Detailed Logs export.
//
// The facade owns the jsPDF instance (letter geometry per orientation) and
// the link annotations; this module owns page painting only and receives the
// sheet geometry from the facade so both stay byte-identical.

const DEFAULT_SCALE = 8; // higher scale for sharper PDFs

/**
 * Paint each page element onto `pdf`, one page per element (raster engine).
 * @param {import("jspdf").jsPDF} pdf - caller-created jsPDF instance; pages
 *   are appended with the same letter geometry the facade constructed it
 *   with.
 * @param {HTMLElement[]} pageElements - rendered page elements, in order
 * @param {Object} [options]
 * @param {number} [options.scale=8] - html2canvas capture scale
 * @param {(status: string, progress: number) => void} [options.onProgress] - optional progress callback
 * @param {number} [options.pageWidthMm] - sheet width in mm (orientation-aware)
 * @param {number} [options.pageHeightMm] - sheet height in mm (orientation-aware)
 */
export const paintRasterPages = async (
  pdf,
  pageElements,
  { scale = DEFAULT_SCALE, onProgress, pageWidthMm, pageHeightMm } = {},
) => {
  // Remove border radius for PDF capture (restored in finally). The resolved
  // page elements are the .InvoiceItem shells — the same elements the legacy
  // pipeline zeroed via host.querySelectorAll(".InvoiceItem").
  const pageItems = pageElements.filter((el) =>
    el.classList.contains("InvoiceItem"),
  );
  const originalStyles = [];
  pageItems.forEach((item) => {
    originalStyles.push(item.style.borderRadius);
    item.style.borderRadius = "0";
  });

  try {
    const { default: html2canvas } = await import("html2canvas");

    // Appended pages mirror the document's orientation, using the legacy
    // idioms: portrait keeps the array format [215.9, 279.4]; landscape uses
    // the "letter" keyword (jsPDF's array-format/orientation swap is
    // ambiguous).
    const isLandscape = pageWidthMm > pageHeightMm;

    for (let i = 0; i < pageElements.length; i++) {
      const pageElement = pageElements[i];
      onProgress?.(
        `Rendering page ${i + 1} of ${pageElements.length}…`,
        Math.round((i / pageElements.length) * 100),
      );

      const canvas = await html2canvas(pageElement, {
        scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        imageSmoothingEnabled: true,
      });

      const imgData = canvas.toDataURL("image/png");

      if (i > 0) {
        if (isLandscape) {
          pdf.addPage("letter", "landscape");
        } else {
          pdf.addPage([pageWidthMm, pageHeightMm], "portrait");
        }
      }

      pdf.addImage(
        imgData,
        "JPEG",
        0,
        0,
        pageWidthMm,
        pageHeightMm,
        undefined,
        "FAST",
      );
    }
  } finally {
    pageItems.forEach((item, index) => {
      item.style.borderRadius = originalStyles[index];
    });
  }
};
